import React, { useMemo, useState, useEffect } from 'react'
import { Segment, Icon, Divider } from 'semantic-ui-react'
import { IStudyBudget } from '../types/studyBudget'
import { formatCurrency } from '../utils/currencyFormat'
import {
  calculatePositionCompletionMetrics
} from '../utils/budgetPositionStats'
import { calculateBudgetTotalWithRebate, calculateDirectCostsAfterRebate } from '../utils/budgetCalculations'
import DataQualityWizard from './DataQualityWizard'
import FormattedTotal from './FormattedTotal'
import { _axios } from '../../../api/service'

interface BudgetSummaryWidgetProps {
  budget: IStudyBudget
  editMode?: boolean
  inputValueRebate?: string
}

interface CalculatedTotals {
  total_budget: number
  total_direct_costs: number
  total_pass_through_costs: number
  total_budget_rounded: number
  total_direct_costs_rounded: number
  total_pass_through_costs_rounded: number
  direct_costs_after_rebate?: number
  rebate?: number
}

const BudgetSummaryWidget: React.FC<BudgetSummaryWidgetProps> = ({
  budget,
  editMode = false,
  inputValueRebate = '',
}) => {
  // State for wizard dialog
  const [wizardOpen, setWizardOpen] = useState(false)
  // State for fresh calculated totals
  const [calculatedTotals, setCalculatedTotals] = useState<CalculatedTotals | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // An amendment is a budget created from a source budget (self-FK on the model).
  const isAmendment = Boolean(budget.source_budget)

  /**
   * Source offer total the deviation badge/line is measured against.
   *
   * Read from `source_snapshot` — the source frozen into this amendment by the INSERT
   * that created it (issue #20 P0). It replaces a second `/calculated-totals/` request
   * aimed at the live source, which both cost a round-trip and let a later edit to the
   * source silently move the deviation. DjangoJSONEncoder emits the Decimal as a string,
   * hence Number(). 0 for non-amendments, which render no deviation at all.
   */
  const sourceTotalBudget = isAmendment
    ? Number(budget.source_snapshot!.total_budget_rounded)
    : 0

  /**
   * Whether a deviation can be computed at all.
   *
   * A snapshot that predates `total_budget_rounded` entering SNAPSHOT_FIELDS, or one written
   * by an older path, has no baseline to measure against — `Number(undefined)` is NaN, and
   * NaN then poisons every figure derived from it. formatCurrency fails fast on NaN by
   * design, so on staging one absent key took the whole budget detail page down rather than
   * one badge.
   *
   * This is not a fallback: there is no sensible number to substitute, and inventing 0 would
   * report "no change" for a comparison that never happened. It is a real state — "no
   * comparable baseline" — and the component already renders it as an em dash when the source
   * total is zero. Same treatment, one more cause.
   */
  const hasComparableSource = isAmendment && Number.isFinite(sourceTotalBudget)

  /**
   * Fetch calculated totals from the API.
   * Triggers on mount and whenever budget positions change (after save/calculate).
   */
  useEffect(() => {
    const fetchCalculatedTotals = async () => {
      const budgetId = typeof budget.id === 'string' ? parseInt(budget.id, 10) : budget.id

      setIsLoading(true)
      try {
        const response = await _axios.get(`/api/budget-module/projects/${budgetId}/calculated-totals/`)
        setCalculatedTotals(response.data)
      } catch (error) {
        console.error('[BudgetSummaryWidget] Failed to fetch calculated totals:', error)
        setCalculatedTotals(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchCalculatedTotals()
    // Re-fetch when budget positions change (after save or apply calculations)
  }, [budget.id, budget.budget_positions, budget.external_positions])

  // Use fresh calculated totals if available, otherwise fall back to saved values
  /*
   * Number() on every one of these, for the same reason sourceTotalBudget above needs it:
   * DRF serialises a DecimalField as a STRING, so these arrive as '2790384.00', not 2790384.
   *
   * A string survives most of what this component does with it — JS coerces for `-`, `*` and
   * comparisons — but NOT for `+`, which concatenates. The fallback total below adds these two
   * together, and on staging that produced '2790384.00' + '921080.00' = '2790384.00921080.00',
   * a string with two decimal points, which Number() then reads as NaN. The deviation became
   * NaN and formatCurrency threw 'Invalid currency value', taking the budget detail page down.
   */
  const directCostsTotal = Number(
    calculatedTotals
      ? calculatedTotals.total_direct_costs_rounded
      : budget.total_direct_costs_rounded !== undefined && budget.total_direct_costs_rounded !== null
        ? budget.total_direct_costs_rounded
        : 0
  )

  const passThroughCostsTotal = Number(
    calculatedTotals
      ? calculatedTotals.total_pass_through_costs_rounded
      : budget.total_pass_through_costs_rounded !== undefined && budget.total_pass_through_costs_rounded !== null
        ? budget.total_pass_through_costs_rounded
        : 0
  )

  // Determine active rebate: live from input (edit mode) or saved value
  const activeRebate = editMode && inputValueRebate
    ? parseFloat(inputValueRebate.replace(',', '.')) || 0
    : budget.rebate || 0

  // Always compute total with rebate applied to direct costs
  const totalBudget = Number(
    activeRebate > 0
      ? calculateBudgetTotalWithRebate(directCostsTotal, passThroughCostsTotal, activeRebate)
      : calculatedTotals
        ? calculatedTotals.total_budget_rounded
        : budget.total_budget_rounded !== undefined && budget.total_budget_rounded !== null
          ? budget.total_budget_rounded
          : directCostsTotal + passThroughCostsTotal
  )

  // Calculate position-based completion metrics
  const dataQualityMetrics = useMemo(() => {
    const budgetPositions = budget.budget_positions || []
    const externalPositions = budget.external_positions || []

    const metrics = calculatePositionCompletionMetrics(budgetPositions, externalPositions)

    return metrics
  }, [budget.budget_positions, budget.external_positions])

  // Deviation of this amendment's offer total vs the frozen source offer total. Available
  // on first paint — the snapshot rides along on the budget, so there is nothing to await.
  const deviationAmount = hasComparableSource ? totalBudget - sourceTotalBudget : 0
  const deviationPercent = hasComparableSource && sourceTotalBudget !== 0
    ? (deviationAmount / sourceTotalBudget) * 100
    : 0
  // Green when the amendment increases the offer, red when it decreases it, neutral at ~zero.
  const deviationColor = deviationAmount > 0.005
    ? '#08D6A1'
    : deviationAmount < -0.005
      ? '#FA5953'
      : '#747678'
  const deviationSign = deviationAmount > 0.005 ? '+' : deviationAmount < -0.005 ? '-' : ''
  const deviationCurrency = hasComparableSource
    ? `${deviationSign}${formatCurrency(Math.abs(deviationAmount))}`
    : '—'
  // '—' when the source total is zero (percentage undefined) or absent (nothing to compare).
  const deviationPercentText = hasComparableSource && sourceTotalBudget !== 0
    ? `${deviationSign}${Math.abs(deviationPercent).toFixed(1)}%`
    : '—'

  return (
    <Segment style={{
      boxShadow: '0 0 25px -5px #15151511',
      border: 'none',
      background: 'white',
      borderRadius: '8px',
      padding: '12px 20px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '28px',
        margin: '0',
        alignItems: 'center'
      }}>
        <div>
          {isAmendment ? (
            // Amendment: render the label inline with the deviation badge so the
            // percentage sits on the same line as "Project Offer Total".
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <p className="formatted-total-label size-default" style={{ margin: 0 }}>
                  Project Offer Total
                </p>
                <span
                  data-testid="amendment-deviation-badge"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: '18px',
                    padding: '0 7px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: deviationColor,
                    backgroundColor: 'rgba(244, 247, 249, 1)',
                    borderRadius: '6px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {deviationPercentText}
                </span>
              </div>
              <FormattedTotal amount={totalBudget} size="large" showLabel={false} />
            </>
          ) : (
            <FormattedTotal
              amount={totalBudget}
              label="Project Offer Total"
              size="large"
            />
          )}
        </div>

        {isAmendment ? (
          // Amendments show no completion bar; instead the amendment name and the
          // currency deviation from the source offer total (green up / red down).
          <div
            data-testid="amendment-deviation-summary"
            style={{ display: 'flex', flexDirection: 'column', textAlign: 'right', justifyContent: 'center' }}
          >
            <span style={{
              fontSize: '13px',
              fontWeight: 'bold',
              color: '#172533',
              lineHeight: '1.3em'
            }}>
              {budget.name}
            </span>
            <span style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: deviationColor,
              lineHeight: '1.3em'
            }}>
              {deviationCurrency}
            </span>
          </div>
        ) : (
          <div
            style={{
              backgroundColor: 'rgba(244, 247, 249, 1)',
              borderRadius: '8px',
              display: 'flex',
              padding: '7px 12px',
              gap: '8px',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            onClick={() => setWizardOpen(true)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(234, 237, 239, 1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(244, 247, 249, 1)'
            }}
          >
            <div style={{
              width: '31.2px',
              height: '31.2px',
              borderRadius: '50%',
              background: `conic-gradient(rgba(8, 214, 161, 1) ${dataQualityMetrics.completionPercentage * 3.6}deg, rgba(190, 200, 195, 1) 0deg)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '17.7px',
                height: '17.7px',
                borderRadius: '50%',
                background: 'rgb(244, 247, 249)'
              }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
              <span style={{
                fontSize: '22px',
                fontWeight: 'bold',
                color: '#08D6A1',
                lineHeight: '1em',
                textAlign: 'left'
              }}>
                {dataQualityMetrics.completionPercentage}%
              </span>
              <span style={{
                fontSize: '11px',
                fontWeight: 'bold',
                color: '#172533',
                textAlign: 'left',
                lineHeight: '1em'
              }}>
                Completion
              </span>
            </div>
          </div>
        )}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        margin: '6px 0 0',
        flexFlow: 'row wrap',
        borderTop: '1px solid rgba(244, 247, 249, 1)',
        paddingTop: '6px',
        gap: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <p style={{ fontSize: '12px', lineHeight: '14px', margin: 0, fontWeight: 'bold' }}>Direct Costs</p>
          <div style={{ display: 'flex', color: '#172533', fontWeight: 'bold', fontSize: '15px', marginLeft: '4px' }}>
            {(() => {
              const displayDirectCosts = activeRebate > 0
                ? calculateDirectCostsAfterRebate(directCostsTotal, activeRebate)
                : directCostsTotal
              return (
                <>
                  <p style={{ margin: 0 }}>{formatCurrency(displayDirectCosts).split(',')[0]}</p>
                  <p style={{ color: '#BEC8C3', margin: 0 }}>,{formatCurrency(displayDirectCosts).split(',')[1]}</p>
                </>
              )
            })()}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <p style={{ fontSize: '12px', lineHeight: '14px', margin: 0, fontWeight: 'bold' }}>PTC:</p>
          <div style={{ display: 'flex', color: '#172533', fontWeight: 'bold', fontSize: '15px', marginLeft: '4px' }}>
            <p style={{ margin: 0 }}>{formatCurrency(passThroughCostsTotal).split(',')[0]}</p>
            <p style={{ color: '#BEC8C3', margin: 0 }}>,{formatCurrency(passThroughCostsTotal).split(',')[1]}</p>
          </div>
        </div>
        {/* Rebate indicator - input is in BudgetDetailPositionsTable */}
        {activeRebate > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <p style={{ fontSize: '12px', lineHeight: '14px', margin: 0, fontWeight: 'bold', color: '#747678' }}>
              Rebate: {activeRebate}%
            </p>
          </div>
        ) : null}
      </div>

      {budget.owner && (
        <>
          <Divider />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            <Icon name="user outline" style={{ color: '#747678' }} />
            <span style={{ color: '#747678' }}>Budget Owner:</span>
            <span style={{ marginLeft: 'auto', fontWeight: '500', color: '#172533' }}>{budget.owner.name}</span>
          </div>
        </>
      )}

      {/* Data Quality Wizard Dialog */}
      <DataQualityWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        positionsWithoutPhases={dataQualityMetrics.positionsWithoutPhases}
        invalidQuantityPositions={dataQualityMetrics.invalidQuantityPositions}
        positionsWithoutTimelines={dataQualityMetrics.positionsWithoutTimelines}
        completionPercentage={dataQualityMetrics.completionPercentage}
        totalEnabledPositions={dataQualityMetrics.totalEnabledPositions}
      />
    </Segment>
  )
}

export default BudgetSummaryWidget